<template>
  <!-- 企业微信端职员列表：在通用职员列表基础上追加企业微信相关的列与操作 -->
  <div class="employee-list-qw">
    <employee-list-common ref="employeeListCommon" @query-list="queryList" options-width="260rem">
      <!--
        追加列表列：企业微信关联情况。

        ★ 已关联时不再显示「已关联」四个字，直接用通讯录组件显示企业微信用户名 ——
          运维/HR 要核对的是「这个职员绑的是哪个企业微信号」，光看「已关联」核不出来。
        ★ 名字后面跟一个「取消关联」：绑错了要能解绑重绑，否则只能改库。
      -->
      <template #columns>
        <yq-table-column label="企业微信" prop="qywxBind" width="240rem" align="center">
          <template v-slot="{row}">
            <template v-if="row.qywxUserId">
              <ww-open-data class="qywx-name" type="userName" :openid="row.qywxUserId"/>
              <table-button el-type="danger" message="确认解除该职员与企业微信的关联？解除后可重新关联"
                            @click="unbindQywx(row)">取消关联</table-button>
            </template>
            <table-button v-else type="write" @click="toBindQywx(row)">关联企业微信</table-button>
          </template>
        </yq-table-column>
      </template>
      <!-- 追加操作按钮：点击后通过 clickRow 通知本组件 -->
<!--      <template #options="{row}">-->
<!--        <table-button type="write" @click="clickRow(row)">测试</table-button>-->
<!--      </template>-->
    </employee-list-common>
  </div>
</template>

<script>
import EmployeeListCommon from "@/views/h5/common/basedata/employee/EmployeeListCommon.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import employeeApi from "@/api/normal/sys/employeeApi";
import merchantEmployeeWxApi from "@/api/normal/sys/merchantEmployeeWxApi";
import qywxUtil from "@/util/qywxUtil";
import Util from "@/util/util";
import * as ww from "@wecom/jssdk";

export default {
  name: "EmployeeListQW",
  components: {
    EmployeeListCommon, YqTableColumn, TableButton
  },
  data() {
    return {
      userInfo: {},       // 登录人员
      lastSearchInfo: {}, // 最近一次查询条件，变更数据后按同样条件重查
    };
  },
  async created() {
    this.userInfo = await Util.getUserInfo();
    // 注册企业微信通讯录组件（ww-open-data 需要先注册才能显示姓名）
    await qywxUtil.ready();
    await qywxUtil.initOpenData();
  },
  methods: {
    /**
     * 查询职员列表：数据由本组件负责查询组装，再交给通用列表展示。
     */
    async queryList(searchInfo) {
      this.lastSearchInfo = searchInfo;
      // 关联查询要用商户id，首次进入时登录信息可能还没返回，这里等一下
      if (!this.userInfo.merchantId) {
        this.userInfo = await Util.getUserInfo();
      }
      const res = await employeeApi.queryList(searchInfo);
      // 补齐企业微信关联情况后再交给子组件，避免列表先渲染成未关联再闪一下
      await this.fillQywxBind(res.records);
      this.$refs.employeeListCommon.setData(res);
      // 数据渲染完成后绑定通讯录组件，显示企业微信姓名
      if (ww) {
        this.$nextTick().then(() => qywxUtil.bindQywxName());
      }
    },
    /**
     * 回填职员的企业微信关联。
     *
     * 一次批量查回再按 employeeId 归组，避免逐行查库；查失败不阻断列表展示，
     * 只是关联列显示为未关联。
     */
    async fillQywxBind(records) {
      if (!records || !records.length) {
        return;
      }
      const employeeIds = records.map(item => item.id).filter(Boolean);
      if (!employeeIds.length) {
        return;
      }
      const list = await merchantEmployeeWxApi.queryByEmployeeIds(this.userInfo.merchantId, employeeIds);
      const bindMap = {};
      (list || []).forEach(bind => {
        if (bind.employeeId) {
          bindMap[bind.employeeId] = bind.qywxUserId;
        }
      });
      records.forEach(row => {
        row.qywxUserId = bindMap[row.id] || "";
      });
    },
    /**
     * 关联企业微信：打开企业微信通讯录选人，选中后把该职员与企业微信用户绑定。
     *
     * 选人取消/失败与绑定失败要分开处理：选人取消是正常操作不必提示，
     * 绑定失败（如该企业微信用户已关联其它职员）由接口层统一提示，这里只记录。
     */
    async toBindQywx(row) {
      await qywxUtil.ready();
      let qywxUserId;
      try {
        qywxUserId = await qywxUtil.selectEmployeeSingle();
      } catch (err) {
        console.log("选择企业微信用户失败", err);
        return;
      }
      if (!qywxUserId) {
        return;
      }
      try {
        await merchantEmployeeWxApi.bind(row.id, qywxUserId);
      } catch (err) {
        console.log("关联企业微信失败", err);
        // 提示已由接口层统一弹出，这里不重复提示
        return;
      }
      this.$message.success("关联成功");
      // 数据由本组件负责查询，变更后按同样条件重查一次即可（不再手工改子组件的数据）
      this.queryList(this.lastSearchInfo);
    },
    /**
     * 解除企业微信关联：关联错了要先解除，才能重新关联。
     *
     * 解除后按同样的查询条件重查一次 —— 数据由本组件负责查询组装，
     * 重查后该行的 qywxUserId 为空，列上自然换回「关联企业微信」按钮。
     */
    async unbindQywx(row) {
      try {
        await merchantEmployeeWxApi.unbind(row.id);
      } catch (err) {
        console.log("解除企业微信关联失败", err);
        // 提示已由接口层统一弹出，这里不重复提示
        return;
      }
      this.$message.success("已解除关联");
      this.queryList(this.lastSearchInfo);
    },
    /**
     * 操作列插槽里的按钮点击：统一通过 clickRow 事件往外抛。
     *
     * 目前只输出日志，用于验证插槽事件打通；后续接业务时在这里分发。
     */
    clickRow(row) {
      console.log("EmployeeListQW clickRow", row);
    }
  }
};
</script>

<style lang="scss" scoped>
.employee-list-qw {
  height: 100%;
}

// 企业微信用户名（通讯录组件）+ 取消关联按钮同格：名字与按钮之间留点间距、居中对齐
// 插槽内容在父组件作用域里渲染，元素带的是本组件的 scope 标记，普通选择器即可命中
.qywx-name {
  margin-right: 8px;
  vertical-align: middle;
}
</style>
